import { FormEvent, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DUPLICATE_EMAIL_SIGNUP_MESSAGE, isPasswordStrong, PASSWORD_REQUIREMENT_MESSAGE } from '@primetime/shared';
import { useAuth } from '../features/auth/AuthContext';

export function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [stageName, setStageName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const successMessage = typeof (location.state as { message?: unknown } | null)?.message === 'string'
    ? (location.state as { message: string }).message
    : '';
  const duplicateEmailError = isRegister && error === DUPLICATE_EMAIL_SIGNUP_MESSAGE;

  useEffect(() => {
    setIsRegister(location.pathname === '/register');
  }, [location.pathname]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (isRegister && !isPasswordStrong(password)) {
      setError(PASSWORD_REQUIREMENT_MESSAGE);
      return;
    }

    try {
      if (isRegister) {
        await register(email, password, stageName);
        navigate('/pricing');
      } else {
        await login(email, password);
        navigate('/crates');
      }
    } catch (err: any) {
      setError(err.message || `${isRegister ? 'Registration' : 'Login'} failed`);
    }
  }

  return (
    <section className="container-shell flex min-h-[75vh] items-center justify-center py-16">
      <form onSubmit={onSubmit} className="glass auth-card w-full max-w-md rounded-3xl p-8" autoComplete="off">
        <div className="text-center">
          <h1 className="text-3xl font-black">
            {isRegister ? 'Create Account' : 'Welcome Back'}
          </h1>
          <div className="mt-4 text-lg font-bold text-white">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              className="text-prime-red hover:underline"
              onClick={() => setIsRegister(!isRegister)}
            >
              {isRegister ? 'Sign in' : 'Sign Up'}
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-5 rounded-xl border border-prime-red/40 bg-prime-red/10 p-3 text-sm text-red-200">
            <p>{error}</p>
            {duplicateEmailError && (
              <div className="mt-2 flex flex-wrap gap-3">
                <Link to="/login" className="font-bold text-prime-red hover:underline">
                  Sign in
                </Link>
                <Link to="/forgot-password" className="font-bold text-prime-red hover:underline">
                  Reset password
                </Link>
              </div>
            )}
          </div>
        )}
        {successMessage && !error && (
          <div className="mt-5 rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-3 text-sm text-emerald-100">
            {successMessage}
          </div>
        )}
        <label className="mt-6 block text-sm font-bold text-white/70">Email</label>
        <input
          className="form-input mt-2"
          type="email"
          value={email}
          autoComplete="off"
          onChange={(e) => setEmail(e.target.value)}
        />
        {isRegister && (
          <>
            <label className="mt-4 block text-sm font-bold text-white/70">Stage Name</label>
            <input
              className="form-input mt-2"
              value={stageName}
              autoComplete="off"
              onChange={(e) => setStageName(e.target.value)}
            />
          </>
        )}
        <div className="mt-4 flex items-center justify-between gap-4">
          <label className="block text-sm font-bold text-white/70">Password</label>
          {!isRegister && (
            <Link to="/forgot-password" className="text-sm font-bold text-prime-red hover:underline">
              Forgot password?
            </Link>
          )}
        </div>
        <input
          className="form-input mt-2"
          type="password"
          value={password}
          autoComplete={isRegister ? 'new-password' : 'off'}
          onChange={(e) => setPassword(e.target.value)}
        />
        {isRegister && (
          <p className="mt-2 text-xs leading-5 text-white/50">
            {PASSWORD_REQUIREMENT_MESSAGE}
          </p>
        )}
        <button className="btn-primary mt-6 w-full" type="submit">
          {isRegister ? 'Sign Up' : 'Log in'}
        </button>
        {isRegister && (
          <div className="mt-6 text-center text-xs text-white/40">
            By signing up, you agree to the{' '}
            <Link to="/terms" className="text-prime-red hover:underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-prime-red hover:underline">
              Privacy Policy
            </Link>
          </div>
        )}
      </form>
    </section>
  );
}
